import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import {
  view,
  errorBoundary,
  withInject,
  ConfigService,
} from '@piwikpro/platform';
import { RouterService } from '@piwikpro/router-crate';
import { ListSidebarPreference } from '@piwikpro/user-preferences';
import {
  View,
  SplitView,
  Sidebar,
  withTranslation,
} from '@piwikpro/ui-components';
import { Outlet, useLocation } from '@tanstack/react-router';
import { Users as UsersService } from '@piwikpro/users-crate';
import { AdministrationState } from '../../redux';
import UsersListOverlay from './UsersListOverlay';

const ASCENDING = '';
const DESCENDING = '-';

const mapStateToProps = (state: AdministrationState) => ({
  users: state.users.list.users,
  user: state.users.details.user.details,
  isLoading: state.users.list.isLoading,
  failed: state.users.list.failed,
  total: state.users.list.total,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  config: ConfigService
  userPreferences: any
  usersApi: UsersService
  Title: any
  routerService: RouterService
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

interface State {
  limit: number
  offset: number
  search: string
  sortBy: string
  sortDirection: string
}

function UsersListElement({
  id, name, caption, onClick,
}) {
  const location = useLocation();
  const isActive = location.pathname.includes(id);
  return (
    <Sidebar.Element
      id={id}
      name={name}
      caption={caption}
      isActive={isActive}
      onClick={onClick}
    />
  );
}

class Users extends React.Component<Props, State> {
  private paginationLimit: number

  private listSidebarPreference: any

  constructor(props) {
    super(props);

    const { config, userPreferences } = this.props;

    this.paginationLimit = config.get('SIDEBAR_LISTS_ITEMS_PER_PAGE');
    this.listSidebarPreference = new ListSidebarPreference(
      userPreferences.load(),
      '/administration/users',
      {
        sortBy: config.get('USERS_DEFAULT_SORT'),
      },
    );

    this.state = {
      limit: config.get('SIDEBAR_LISTS_ITEMS_PER_PAGE'),
      offset: 0,
      search: '',
      sortBy: this.listSidebarPreference.sortBy,
      sortDirection: '',
    };

    this.moveToDetailsView = this.moveToDetailsView.bind(this);
    this.moveToInviteView = this.moveToInviteView.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.loadMoreListItems = this.loadMoreListItems.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
  }

  async componentDidMount() {
    const { usersApi, config } = this.props;
    const {
      limit,
      offset,
      search,
      sortBy,
    } = this.state;
    let sortDirection = ASCENDING;

    if (![config.get('USERS_DEFAULT_SORT'), 'role'].includes(this.listSidebarPreference.sortBy)) {
      sortDirection = DESCENDING;
    }

    this.setState({
      sortDirection,
    });

    try {
      await usersApi.fetchUsers({
        limit,
        offset,
        search,
        sortBy,
        sortDirection,
      });
      // eslint-disable-next-line no-empty
    } catch (err) { }
  }

  handleSearchChange(searchValue) {
    this.setState({
      offset: 0,
      search: searchValue.target.value,
    });
  }

  async handleSearch() {
    const { usersApi } = this.props;
    const {
      limit,
      search,
      sortBy,
      sortDirection,
    } = this.state;

    if (!/^\s+$/i.test(search)) { // avoid API requests for white-characters only
      const trimmedSearch = search.trim();

      try {
        await usersApi.fetchUsers({
          limit,
          offset: 0,
          search: trimmedSearch,
          sortBy,
          sortDirection,
        });
      // eslint-disable-next-line no-empty
      } catch (err) { }
    }
  }

  async loadMoreListItems(newOffset) {
    const {
      limit,
      search,
      sortBy,
      sortDirection,
    } = this.state;
    const { usersApi } = this.props;

    this.setState({
      offset: newOffset,
    });

    try {
      await usersApi.fetchUsers({
        limit,
        offset: newOffset,
        search,
        sortBy,
        sortDirection,
      });
    // eslint-disable-next-line no-empty
    } catch (err) { }
  }

  moveToInviteView() {
    const { routerService } = this.props;

    routerService.openAdministrationOverlay('/m/invite', { state: this.state });
  }

  moveToDetailsView(userId: string) {
    const { routerService } = this.props;

    routerService.push(
      { to: '/administration/users/$userId', params: { userId: String(userId) } },
      { state: this.state },
    );
  }

  render() {
    const {
      t,
      users,
      isLoading,
      total,
      config,
      failed,
      usersApi,
      Title,
      userPreferences,
    } = this.props;
    const {
      limit,
      offset,
      search,
      sortBy,
    } = this.state;

    const listSidebarOptions = {
      sortItems: [{
        id: 'users-sidebar-sort-options',
        items: [{
          name: t('users.list.sortOptions.email'),
          value: 'email',
          id: 'email',
        },
        {
          name: t('users.list.sortOptions.type'),
          value: 'role',
          id: 'role',
        },
        {
          name: t('users.list.sortOptions.addedAt'),
          value: 'addedAt',
          id: 'addedAt',
        }],
      }],
      onSortByItemSelected: (sortByValue) => {
        this.listSidebarPreference.sortBy = sortByValue;

        userPreferences.persist(this.listSidebarPreference);

        let sortDirection = ASCENDING;

        if (![config.get('USERS_DEFAULT_SORT'), 'role'].includes(this.listSidebarPreference.sortBy)) {
          sortDirection = DESCENDING;
        }

        this.setState({
          offset: 0,
          sortBy: this.listSidebarPreference.sortBy,
          sortDirection,
        }, async () => {
          try {
            await usersApi.fetchUsers({
              limit,
              offset: 0,
              search,
              sortBy: this.listSidebarPreference.sortBy,
              sortDirection,
            });
          // eslint-disable-next-line no-empty
          } catch (err) { }
        });
      },
      sortDefault: sortBy,
    };

    const getLabelForUser = (user) => {
      let result;

      switch (sortBy) {
        case 'addedAt':
          result = t('components:date.short', { date: new Date(user.attributes.addedAt) });
          break;
        case 'role':
          result = t(`users.roles.${user.attributes.role.toLowerCase()}`);
          break;
        default:
          result = null;
          break;
      }

      return result;
    };

    return (
      <Title part={t('users.title')}>
        <SplitView
          leftSidebar={{
            canBeHideManually: true,
            sidebar: (
              <div style={{ width: '400px', height: '100%' }}>
                <Sidebar>
                  <Sidebar.Search
                    value={search}
                    placeholder={t('users.list.searchPlaceholder')}
                    onSearchChange={this.handleSearchChange}
                    onSearch={this.handleSearch}
                    debounceTime={config.get('SIDEBAR_LISTS_SEARCH_DELAY')}
                  />
                  <Sidebar.ListOptions
                    sort={{
                      list: listSidebarOptions.sortItems,
                      onChange: listSidebarOptions.onSortByItemSelected,
                      value: this.listSidebarPreference.sortBy,
                    }}
                  />
                  {!isLoading && users.length === 0 && (
                    <Sidebar.Empty type="subdued">
                      {t('users.list.no-items')}
                    </Sidebar.Empty>
                  )}
                  {failed && (
                    <Sidebar.Empty type="error">
                      {t('ListSidebar.error')}
                    </Sidebar.Empty>
                  )}
                  {isLoading ? (
                    <Sidebar.ElementsLoading />
                  ) : (
                    <Sidebar.List>
                      {users.map(user => (
                        <UsersListElement
                          id={user.id}
                          key={user.id}
                          name={user.attributes.email as string}
                          caption={getLabelForUser(user)}
                          onClick={this.moveToDetailsView}
                        />
                      ))}
                    </Sidebar.List>
                  )}
                  {users.length < total && (
                    <Sidebar.LoadMore
                      onClick={() => this.loadMoreListItems(offset + this.paginationLimit)}
                    />
                  )}
                  <Sidebar.AddSection
                    mainButton={{
                      onClick: this.moveToInviteView,
                      text: t('users.list.invite-user.button'),
                      tooltip: undefined,
                      disabled: false,
                      dataId: undefined,
                    }}
                  />
                </Sidebar>
              </div>
            ),
          }}
        >
          <View fullWidth fullHeight>
            <Outlet />
            <UsersListOverlay />
          </View>
        </SplitView>
      </Title>
    );
  }
}

export default connector(withInject<InjectedProps>({
  usersApi: 'UsersCrate.users',
  config: 'config',
  userPreferences: 'userPreferences',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
})(
  view({ role: 'OWNER' })(withTranslation('administration')(
    errorBoundary(Users as any),
  )),
));
